import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { Skeleton } from 'primereact/skeleton';
import AuthContext from '../../../../auth/AuthContext';
import FAQService from '../../../../services/FAQService';
import AlertService from '../../../../services/AlertService';
import { Button } from 'react-bootstrap';
import FaqCard from './FaqCard';
import ConfirmDialogBox from '../../../confirmDialog/ConfirmDialogBox';

const FaqList = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [faqs, setFAQs] = useState([]);
  const [filteredFAQs, setFilteredFAQs] = useState([]);
  const [activeIndex, setActiveIndex] = useState();
  const [faqId, setFaqId] = useState(0);
  const authContext = useContext(AuthContext);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [openMenuCardId, setOpenMenuCardId] = useState(null);

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const groupFAQs = useCallback((faqs) => {
    const parentGroups = Object.groupBy(faqs, d => d.typeDesc || '');
    return Object.entries(parentGroups).map(([tag, items]) => ({
      key: tag,
      items,
    }));
  }, []);

const loadFAQs = useCallback(async (signal) => { 
    setLoading(true);
    setSearchValue('');
    try {
      const data = await FAQService.GetAllFAQs(
        authContext,
        signal 
      );

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setFAQs(data.data);
        setFilteredFAQs(groupFAQs(data.data));
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext, groupFAQs]);

useEffect(() => {
    const controller = new AbortController(); 
    loadFAQs(controller.signal); 
    return () => controller.abort(); 
  }, [loadFAQs]);

  const handleSubmit = useCallback(() => {
    setFilteredFAQs(
      groupFAQs(
        faqs.filter(f =>
          f.question.toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    );
  }, [faqs, groupFAQs, searchValue]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSubmit();
    }, 100);
    return () => clearTimeout(timeout);
  }, [handleSubmit, searchValue]);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const addNew = (e) => {
    e.preventDefault();
    navigate('/masters-faq/add');
  };

  const onDeleteHandler = async () => {
    if (faqId > 0) {
      setShowDeleteConfirm(false);
      setLoading(true);
      let data = await FAQService.Remove(faqId, authContext);
      if (data.isSuccess) {
        AlertService.success("FAQ details are deleted successfully!");
        loadFAQs();
      } else {
        AlertService.error(data.errorMessage);
      }
      setLoading(false);
      setFaqId(0);
    }
  };

  const deleteCard = (id) => {
    setFaqId(id);
    setShowDeleteConfirm(true);
  };

  const handleOpenMenu = (faqId) => setOpenMenuCardId(faqId);
  const handleCloseMenu = () => setOpenMenuCardId(null);

  useEffect(() => {
    const handleScroll = () => {
      setOpenMenuCardId(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  return (
    <Fragment>
      <div className="d-flex justify-content-end align-items-center flex-wrap container-fluid mt-3">
        <div style={{ maxWidth: '470px', width: '100%' }}>
          <div className="position-relative w-100">
            <div className="position-absolute top-50 start-0 translate-middle-y ps-3">
              <FaSearch className="fas fa-search text-muted cursor-pointer" onClick={handleSubmit} />
            </div>
            <input
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className="form-control ps-5  custom-input-color py-2"
              placeholder="Search..."
              style={{
                paddingLeft: '2.5rem',
                paddingRight: '1rem',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem'
              }}
            />
          </div>
        </div>
        <Button variant='primary' className='ms-3 py-2 mt-2 mt-md-0' onClick={addNew}>Add New Question</Button>
      </div>

      {loading ? (
        Array.from({ length: 8 }).map((_, idx) => (
          <div key={idx} className='col-12 p-2 mt-2'>
            <Skeleton width='100%' height='5rem'></Skeleton>
          </div>
        ))
      ) : filteredFAQs.length > 0 ? (
        filteredFAQs.map((parentGroup, i) => (
          <Fragment key={i}>
            <div className="d-flex mt-2">
              <div className="ms-1 text-capitalize px-2 py-2">
                {parentGroup.key || ''}
              </div>
            </div>
            <div>
              <div className="accordion-wrapper">
                {parentGroup.items && parentGroup.items.length > 0 && parentGroup.items.map((item) =>
                  <FaqCard
                    key={item.id}
                    item={item}
                    activeIndex={activeIndex}
                    toggleAccordion={toggleAccordion}
                    deleteCard={deleteCard}
                    isMenuOpen={openMenuCardId === item.id}
                    onOpenMenu={handleOpenMenu}
                    onCloseMenu={handleCloseMenu}
                  />
                )}
              </div>
            </div>
          </Fragment>
        ))
      ) : (
        <div className="text-center text-muted mt-4">No FAQs found.</div>
      )}

      <ConfirmDialogBox
        visible={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={onDeleteHandler}
        title="Are you sure you want to delete this quiz?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </Fragment>
  )
}

export default FaqList;
